import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  IconButton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  useLazyGetAttendanceByDateRangeQuery,
  useDeleteAttendanceMutation,
} from "../../../redux/api/attendance";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import AgentSelect from "../Salary/AgentSelect";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
const AttendanceList = ({ onEdit, onRefetch }) => {
  const currentAgency = useSelector(selectCurrentagency);

  const [filters, setFilters] = useState({
    fromDate: dayjs().startOf("month"),
    toDate: dayjs(),
    agentId: "",
  });

  // Queries & Mutations
  const [trigger, { data, isFetching }] =
    useLazyGetAttendanceByDateRangeQuery();
  const [deleteAttendance, { isLoading: deleting }] =
    useDeleteAttendanceMutation();

  // Get Records
  const handleGetRecords = () => {
    trigger({
      fromDate: filters.fromDate?.format("YYYY-MM-DD"),
      toDate: filters.toDate?.format("YYYY-MM-DD"),
      agencyId: currentAgency?.agencyid,
      agentId: filters.agentId || null,
    });
  };

  useEffect(() => {
    if (onRefetch) {
      onRefetch(handleGetRecords);
    }
  }, [onRefetch]);

  // Delete Record
  const handleDelete = async (id) => {
    try {
      await deleteAttendance(id).unwrap();
      // Refresh list after delete
      handleGetRecords();
    } catch (err) {
      console.error("Failed to delete record:", err);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg">
        <Box sx={{ mt: 5, p: 3, borderRadius: 2, boxShadow: 3 }}>
          <Typography variant="h5" mb={3}>
            Attendance Records
          </Typography>

          {/* Filters */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="From Date"
                value={filters.fromDate}
                onChange={(val) =>
                  setFilters((prev) => ({ ...prev, fromDate: val }))
                }
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <DatePicker
                label="To Date"
                value={filters.toDate}
                onChange={(val) =>
                  setFilters((prev) => ({ ...prev, toDate: val }))
                }
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <AgentSelect
                agencyId={currentAgency?.agencyid}
                value={filters.agentId}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, agentId: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                sx={{ color: "white", padding: "8px 0px" }}
                onClick={handleGetRecords}
                fullWidth
              >
                Get Record
              </Button>
            </Grid>
          </Grid>

          {/* Results */}
          {isFetching ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <CircularProgress />
            </Box>
          ) : data && data.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell>
                      <b>Agent ID</b>
                    </TableCell>
                    <TableCell>
                      <b>Date</b>
                    </TableCell>
                    <TableCell>
                      <b>Status</b>
                    </TableCell>
                    <TableCell>
                      <b>Geo Point</b>
                    </TableCell>
                    <TableCell>
                      <b>In Time</b>
                    </TableCell>
                    <TableCell>
                      <b>Out Time</b>
                    </TableCell>
                    <TableCell>
                      <b>Overtime</b>
                    </TableCell>
                    <TableCell>
                      <b>Deduction</b>
                    </TableCell>
                    <TableCell>
                      <b>Attendance Method</b>
                    </TableCell>
                    <TableCell>
                      <b>Action</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.agentid}</TableCell>
                      <TableCell>
                        {dayjs(row.atDate).format("YYYY-MM-DD")}
                      </TableCell>
                      <TableCell>
                        {row.status === 1
                          ? "Present"
                          : row.status === 2
                          ? "Absent"
                          : "Leave"}
                      </TableCell>
                      <TableCell>{row.geopoint || "-"}</TableCell>
                      <TableCell>
                        {row.intime ? dayjs(row.intime).format("HH:mm") : "-"}
                      </TableCell>
                      <TableCell>
                        {row.outime ? dayjs(row.outime).format("HH:mm") : "-"}
                      </TableCell>
                      <TableCell>{row.overtime ?? "-"}</TableCell>
                      <TableCell>{row.deduction ?? "-"}</TableCell>
                      <TableCell>
                        {row.attendancemethod === 0
                          ? "Manual"
                          : row.attendancemethod === 1
                          ? "Machine"
                          : row.attendancemethod === 2
                          ? "Geo"
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {/* Edit */}
                        <IconButton color="primary" onClick={() => onEdit(row)}>
                          <ModeEditIcon />
                        </IconButton>

                        {/* Delete */}
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(row.id)}
                          disabled={deleting}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography textAlign="center" mt={2}>
              No records found.
            </Typography>
          )}
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default AttendanceList;
